import {
  batch,
  computed,
  signal,
  Signal,
  useSignalEffect,
} from '@preact/signals-react';
import {
  AiChatReference,
  AiChatMessage,
  chatNow,
  CustomEventData,
  RunEvent,
  RunResponse,
  RunResponseContent,
  SelectedAgent,
  ToolCall,
} from '@/features/ai-agents/shared/models';
import { routes } from '@/webapi/routes';
import { getQueryParam } from '@/utils/browser';
import { truncate } from '@/utils/types';
import { getJsonMarkdown } from '@/features/ai-agents/shared/message-format-processor';
import { createSignalsContext } from '@/signals/signal-context';
import {
  SessionListEntry,
  useAiMetadataApi,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import { useAIResponseStream } from '@/features/ai-agents/shared/use-ai-response-stream';
import { useAIAuthHeaders } from '@/features/ai-agents/shared/use-ai-auth-headers';
import { sanitizeSelectorToName } from '@/features/editor/widgets/ai-section-editor/sanitize';

export interface ChatSessionState {
  currentSessionId: Signal<string>;
  previousSessionId: Signal<string>;
  messages: Signal<AiChatMessage[]>;
  lastMessageStream: Signal<AiChatMessage | null>;
  abortController: Signal<undefined | AbortController>;

  isProcessing: Signal<boolean>;
  isLoadingSession: Signal<boolean>;
  streamUrl?: Signal<string>;
  isLocalHost?: Signal<boolean>;

  processedEvents?: Signal<string[]>;
  references: Signal<AiChatReference[]>;
}

export interface ChatSessionActions extends ChatSessionContextProps {
  loadSession: () => Promise<void>;
  sendMessage: (msg: AiChatMessage) => Promise<void>;
  abortMessage: () => Promise<void>;
  retryLastMessage: () => Promise<void>;
  addReference: (ref: AiChatReference) => void;
  removeReference: (ref: AiChatReference) => void;
  clearReferences: () => void;
}

export interface ChatSessionContextProps {
  agent: SelectedAgent;
  initialSessionId?: string;
  userId?: string;
  onNewSession?: (session: SessionListEntry) => void;
  onNewMessageSent?: (userMessage: AiChatMessage) => void;
  onNewReply?: (
    agentMessage: AiChatMessage,
    userMessage: AiChatMessage,
    sessionId?: string,
  ) => void;
  onSessionLoaded?: (messages: AiChatMessage[]) => void;
  onLoadingChanged?: (loading: boolean) => void;
  sessionData?: Record<string, any>;
  externalReferences?: Signal<AiChatReference[]>;
}

function createChatSessionState({
  agent,
  initialSessionId,
  externalReferences,
}: ChatSessionContextProps): ChatSessionState {
  const isProcessing = signal<boolean>(false);
  const isLoadingSession = signal<boolean>(false);
  const currentSessionId = signal<string>(initialSessionId ?? ``);
  const previousSessionId = signal<string>(``);

  const messages = signal<AiChatMessage[]>([]);
  const lastMessageStream = signal<AiChatMessage | null>(null);
  const processedEvents = signal<string[]>([]);
  const references = externalReferences ?? signal<AiChatReference[]>([]);

  const streamUrl = computed(() => {
    if (!agent) return ``;
    return isLocalHost.value
      ? routes.convertToLocalAiUrl(routes.aiAgentRun(agent))
      : routes.aiAgentRun(agent);
  });

  const abortController = signal<AbortController | undefined>(undefined);

  const isLocalHost = signal(getQueryParam(`local-ai`) === `1`);

  return {
    currentSessionId,
    previousSessionId,
    isLoadingSession,
    isProcessing,
    messages,
    lastMessageStream,
    streamUrl,
    isLocalHost,
    abortController,
    processedEvents,
    references,
  };
}

function hookChatSessionState(
  state: ChatSessionState,
  props?: ChatSessionContextProps,
): ChatSessionActions {
  const { streamResponse } = useAIResponseStream();
  const { headers, user } = useAIAuthHeaders(state.isLocalHost.value);
  const {
    onNewReply,
    onNewMessageSent,
    onNewSession,
    onLoadingChanged,
    onSessionLoaded,
    userId,
    initialSessionId,
    agent,
  } = props;
  const {
    isProcessing,
    isLoadingSession,
    currentSessionId,
    previousSessionId,
    messages,
    lastMessageStream,
    streamUrl,
    abortController,
    isLocalHost,
    processedEvents,
    references,
  } = state;

  const { getSession } = useAiMetadataApi(isLocalHost.value);

  useSignalEffect(() => onLoadingChanged?.(isProcessing.value));

  function generateClientDependencies(sentData: Record<string, string>) {
    const sessionData = {
      user_first_name: user?.store?.firstName || ``,
      user_last_name: user?.store?.lastName || ``,
      domain: user?.store?.domain || ``,
      ...(props?.sessionData || {}),
      ...(sentData || {}),
    };

    return JSON.stringify(sessionData);
  }

  const sendMessage = async (msg: AiChatMessage) => {
    try {
      processedEvents.value = [];
      isProcessing.value = true;

      msg.references = [...(msg.references || []), ...references.value];

      const agentMsg = {
        role: `agent`,
        content: ``,
        tool_calls: [],
        streamingError: false,
        created_at: chatNow() + 1,
        images: msg?.images,
      } as AiChatMessage;

      messages.value = [...messages.value, msg, agentMsg];
      lastMessageStream.value = { ...agentMsg };
      onNewMessageSent && onNewMessageSent(msg);

      let lastContent = ``;
      let newSessionId = currentSessionId.value;
      const formData = new FormData();

      const updateLastMessageStream = (
        updater: (current: AiChatMessage) => AiChatMessage,
      ) => {
        const current = lastMessageStream.peek();
        if (current && current.role === `agent`) {
          lastMessageStream.value = updater(current);
        }
      };

      const commitLastMessageStream = (
        nextMessage?: AiChatMessage,
      ): AiChatMessage[] => {
        const currentMessages = messages.peek();
        if (!currentMessages.length) return currentMessages;

        const finalMessage =
          nextMessage ??
          lastMessageStream.peek() ??
          currentMessages[currentMessages.length - 1];
        if (!finalMessage || finalMessage.role !== `agent`) {
          return currentMessages;
        }

        const nextMessages = [...currentMessages];
        const lastIndex = nextMessages.length - 1;
        if (nextMessages[lastIndex]?.role === `agent`) {
          nextMessages[lastIndex] = finalMessage;
          messages.value = nextMessages;
        }
        return nextMessages;
      };

      const referencesPayloadChain = msg.references
        ?.map((ref) =>
          typeof ref.payload === `string`
            ? ref.payload
            : JSON.stringify(ref.payload),
        )
        .join(`\n`);

      const messageContent = referencesPayloadChain
        ? `${referencesPayloadChain}\n${msg.content}`
        : msg.content;

      formData.append(`message`, messageContent);
      formData.append(`stream`, `true`);
      formData.append(`session_id`, currentSessionId.value ?? ``);
      formData.append(`user_id`, userId);
      formData.append(
        `metadata`,
        JSON.stringify({
          user: user.store.email,
          alias: user.store.alias,
        }),
      );
      formData.append(`vsly_images`, JSON.stringify(msg.images || []));

      msg?.contextualAttachments?.forEach((attachment) => {
        formData.append(
          `attachment##${sanitizeSelectorToName(attachment.id)}##${
            attachment.type
          }`,
          attachment.payload,
        );
      });

      if (user?.store?.alias) {
        const clientDependencies = generateClientDependencies(msg.session_data);
        formData.append(`dependencies`, clientDependencies);
      }

      abortController.value = new AbortController();
      references.value = [];
      try {
        await streamResponse({
          headers,
          apiUrl: streamUrl.value,
          abortController: abortController.value,
          requestBody: formData,
          onChunk: (chunk: RunResponse) => {
            if (
              agent.type === `team` &&
              (chunk.event === RunEvent.RunContent ||
                chunk.event === RunEvent.RunCompleted)
            ) {
              return;
            }

            if (
              chunk.event === RunEvent.RunStarted ||
              chunk.event === RunEvent.ReasoningStarted ||
              chunk.event === RunEvent.TeamReasoningStarted ||
              chunk.event === RunEvent.TeamRunStarted
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              newSessionId = chunk.session_id as string;
              if (newSessionId) {
                batch(() => {
                  previousSessionId.value = newSessionId;
                  currentSessionId.value = newSessionId;
                });
              }
              if (
                (!initialSessionId || initialSessionId !== chunk.session_id) &&
                chunk.session_id
              ) {
                onNewSession &&
                  onNewSession({
                    session_id: chunk.session_id,
                    session_name: truncate(msg.content, 100),
                    title: truncate(msg.content, 100),
                    created_at: chunk.created_at ?? chatNow(),
                  });
              }
            } else if (
              chunk.event === RunEvent.ToolCallStarted ||
              chunk.event === RunEvent.TeamToolCallStarted
            ) {
              updateLastMessageStream((current) => ({
                ...current,
                tool_calls: processChunkToolCalls(chunk, current.tool_calls),
              }));
            } else if (
              chunk.event === RunEvent.RunContent ||
              chunk.event === RunEvent.TeamRunContent
            ) {
              const lastMessage = lastMessageStream.peek();
              if (
                lastMessage &&
                lastMessage.role === `agent` &&
                typeof chunk.content === `string`
              ) {
                const uniqueContent = chunk.content.replace(lastContent, ``);
                lastContent = chunk.content;

                updateLastMessageStream((current) => ({
                  ...current,
                  content: `${current.content}${uniqueContent}`,
                  tool_calls: processChunkToolCalls(chunk, current.tool_calls),
                  extra_data: {
                    ...current.extra_data,
                    reasoning_steps:
                      chunk.extra_data?.reasoning_steps ??
                      current.extra_data?.reasoning_steps,
                    references:
                      chunk.extra_data?.references ??
                      current.extra_data?.references,
                  },
                  created_at: chunk.created_at ?? current.created_at,
                  images: chunk.images ?? current.images,
                  audio: chunk.audio ?? current.audio,
                }));
              } else if (
                lastMessage &&
                lastMessage.role === `agent` &&
                !!chunk?.content &&
                typeof chunk?.content !== `string`
              ) {
                const jsonBlock = getJsonMarkdown(chunk?.content);
                lastContent = jsonBlock;

                updateLastMessageStream((current) => ({
                  ...current,
                  content: `${current.content}${jsonBlock}`,
                }));
              } else if (
                lastMessage &&
                chunk.response_audio?.transcript &&
                typeof chunk.response_audio?.transcript === `string`
              ) {
                const { transcript } = chunk.response_audio;
                updateLastMessageStream((current) => ({
                  ...current,
                  response_audio: {
                    ...current.response_audio,
                    transcript: `${
                      current.response_audio?.transcript || ``
                    }${transcript}`,
                  },
                }));
              }
            } else if (
              chunk.event === RunEvent.ReasoningCompleted ||
              chunk.event === RunEvent.TeamReasoningCompleted
            ) {
              updateLastMessageStream((current) => ({
                ...current,
                extra_data: {
                  ...current.extra_data,
                  reasoning_steps:
                    chunk.extra_data?.reasoning_steps ??
                    current.extra_data?.reasoning_steps,
                },
              }));
            } else if (chunk.event === RunEvent.CustomEvent) {
              const customEvent = chunk as unknown as CustomEventData;
              if (customEvent.kind && customEvent.content) {
                const payload = JSON.stringify(customEvent);
                const codeBlock = `\n\`\`\`vsly/json/custom-event/${customEvent.kind}\n${payload}\n\`\`\`\n`;
                updateLastMessageStream((current) => ({
                  ...current,
                  content: `${current.content}${codeBlock}`,
                }));
              }
            } else if (
              chunk.event === RunEvent.RunError ||
              chunk.event === RunEvent.TeamRunError
            ) {
              const errorContent = chunk.content as string;
              if (typeof chunk.content === `string`) {
                updateLastMessageStream((current) => ({
                  ...current,
                  streamingError: true,
                  content: `Agent returned with error: ${errorContent}`,
                }));
                commitLastMessageStream();
                lastMessageStream.value = null;
              }
            } else if (
              chunk.event === RunEvent.RunCompleted ||
              chunk.event === RunEvent.TeamRunCompleted
            ) {
              const current =
                lastMessageStream.peek() ??
                messages.peek()[messages.peek().length - 1];
              let updatedContent = current?.content ?? ``;
              if (typeof chunk.content === `string`) {
                updatedContent = chunk.content;
              } else if (chunk?.content) {
                try {
                  updatedContent = JSON.stringify(chunk.content);
                } catch {
                  updatedContent = `Error parsing response`;
                }
              }

              const completedMessage = {
                ...(current || agentMsg),
                run_id: chunk.run_id,
                content: updatedContent,
                tool_calls: processChunkToolCalls(
                  chunk,
                  current?.tool_calls || [],
                ),
                images: chunk.images ?? current?.images,
                response_audio: chunk.response_audio,
                created_at:
                  chunk.created_at ?? current?.created_at ?? chatNow(),
                extra_data: {
                  reasoning_steps:
                    chunk.extra_data?.reasoning_steps ??
                    current?.extra_data?.reasoning_steps,
                  references:
                    chunk.extra_data?.references ??
                    current?.extra_data?.references,
                },
              } as AiChatMessage;

              const newMessages = commitLastMessageStream(completedMessage);
              processedEvents.value = [];
              if (onNewReply) {
                onNewReply(
                  newMessages?.[newMessages.length - 1],
                  newMessages?.[newMessages.length - 2],
                  currentSessionId.value,
                );
              }
              lastMessageStream.value = null;
            }
          },
          onError: (error: Error) => {
            processedEvents.value = [];
            console.error(`Error in AI response stream:`, error);

            updateLastMessageStream((current) => ({
              ...current,
              streamingError: true,
              content:
                error.message ||
                `Agent returned with error: An error occurred while processing your request`,
            }));
            commitLastMessageStream();
            lastMessageStream.value = null;

            isProcessing.value = false;
            abortController.value = undefined;
          },
          onComplete: () => {
            isProcessing.value = false;
            abortController.value = undefined;
            processedEvents.value = [];
          },
        });
      } catch (streamError) {
        processedEvents.value = [];
        console.error(`Error initiating AI response stream:`, streamError);

        let errorMessage = `Agent returned with error: `;
        if (streamError instanceof Error) {
          let msg = streamError.message;
          if (
            msg.includes(`Part exceeded maximum size`) ||
            msg.includes(`1024KB`)
          ) {
            msg = `Section is too big, try to split the section into smaller parts and resend.`;
          }
          errorMessage += msg;
        } else {
          errorMessage += `An error occurred while processing your request`;
        }

        updateLastMessageStream((current) => ({
          ...current,
          streamingError: true,
          content: errorMessage,
        }));
        commitLastMessageStream();
        lastMessageStream.value = null;

        isProcessing.value = false;
        abortController.value = undefined;
      }
    } catch (error) {
      console.error(`Error in sendMessage:`, error);
    } finally {
      isProcessing.value = false;
    }
  };

  const loadSession = async (): Promise<void> => {
    isLoadingSession.value = true;
    messages.value = await getSession(agent, currentSessionId?.value, userId);
    lastMessageStream.value = null;
    isLoadingSession.value = false;
    onSessionLoaded?.(messages.peek());
  };

  const abortMessage = async (): Promise<void> => {
    if (abortController?.peek()?.abort) {
      abortController.value.abort();
      messages.value = [...messages.value.slice(0, messages.value.length - 2)];
      lastMessageStream.value = null;
    }
  };

  const retryLastMessage = async (): Promise<void> => {
    const currentMessages = messages.peek();
    if (currentMessages.length < 2) return;

    // Get the last user message (second to last message)
    const lastUserMessage = currentMessages[currentMessages.length - 2];

    // Remove the last two messages (user + agent error)
    messages.value = [...currentMessages.slice(0, currentMessages.length - 2)];

    // Resend the user message
    if (lastUserMessage && lastUserMessage.role === `user`) {
      await sendMessage(lastUserMessage);
    }
  };

  const addReference = (ref: AiChatReference) => {
    references.value = [ref];
  };

  const removeReference = (ref: AiChatReference) => {
    references.value = references.value.filter((r) => r !== ref);
  };

  const clearReferences = () => {
    references.value = [];
  };

  return {
    loadSession,
    sendMessage,
    abortMessage,
    retryLastMessage,
    addReference,
    removeReference,
    clearReferences,
    ...props,
  };
}

export const [ChatSessionContextProvider, _, useChatSession] =
  createSignalsContext(createChatSessionState, hookChatSessionState);

function processToolCall(toolCall: ToolCall, prevToolCalls: ToolCall[] = []) {
  const toolCallId =
    toolCall.tool_call_id || `${toolCall.tool_name}-${toolCall.created_at}`;

  const existingToolCallIndex = prevToolCalls.findIndex(
    (tc) =>
      (tc.tool_call_id && tc.tool_call_id === toolCall.tool_call_id) ||
      (!tc.tool_call_id &&
        toolCall.tool_name &&
        toolCall.created_at &&
        `${tc.tool_name}-${tc.created_at}` === toolCallId),
  );
  if (existingToolCallIndex >= 0) {
    const updatedToolCalls = [...prevToolCalls];
    updatedToolCalls[existingToolCallIndex] = {
      ...updatedToolCalls[existingToolCallIndex],
      ...toolCall,
    };
    return updatedToolCalls;
  }
  return [...prevToolCalls, toolCall];
}

function processChunkToolCalls(
  chunk: RunResponseContent | RunResponse,
  existingToolCalls: ToolCall[] = [],
) {
  let updatedToolCalls = [...existingToolCalls];
  // Handle new single tool object format
  if (chunk.tool) {
    updatedToolCalls = processToolCall(chunk.tool, updatedToolCalls);
  }
  // Handle legacy tools array format
  if (chunk.tools && chunk.tools.length > 0) {
    chunk?.tools.forEach((toolCall) => {
      updatedToolCalls = processToolCall(toolCall, updatedToolCalls);
    });
  }

  return updatedToolCalls;
}
